
import React from "react";
import OptimizedImage from "@/components/OptimizedImage";
import SiteLocationInfo from "./SiteLocationInfo";
import SiteServicesDisplay from "./SiteServicesDisplay";
import SiteMetaInfo from "./SiteMetaInfo";

interface SiteDetailHeroProps {
  siteName: string;
  city?: string;
  services?: string[];
  websiteUrl?: string;
  proximityInfo?: string;
  phone?: string;
  email?: string;
  isMiniSite?: boolean;
}

const SiteDetailHero: React.FC<SiteDetailHeroProps> = ({
  siteName,
  city,
  services = [],
  websiteUrl,
  proximityInfo,
  phone,
  email,
  isMiniSite = false,
}) => {
  return (
    <section className="relative bg-gradient-to-br from-fabre-gray via-fabre-darkteal to-fabre-gray py-20 text-white overflow-hidden">
      {/* Background Image from Supabase */}
      <div className="absolute inset-0 z-0">
        <OptimizedImage
          src="https://ixphrmuptpnhyapjghvs.supabase.co/storage/v1/object/public/site-images/hero-recyclage-background.jpg"
          alt="Centre de recyclage et gestion des déchets"
          className="w-full h-full object-cover opacity-20"
          fallbackSrc="https://images.unsplash.com/photo-1605810230434-7631ac76ec81?w=1280&q=80"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-fabre-gray/80 via-fabre-darkteal/70 to-fabre-gray/80"></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        <div className="absolute top-10 left-10 w-20 h-20 border-2 border-fabre-green/30 rounded-full"></div>
        <div className="absolute top-32 right-20 w-16 h-16 border-2 border-fabre-teal/40 rounded-full"></div>
        <div className="absolute bottom-20 left-1/4 w-12 h-12 border-2 border-fabre-lightgreen/50 rounded-full"></div>
      </div>

      {/* Wave decoration at bottom */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg
          className="relative block w-full h-[60px]"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 100"
          preserveAspectRatio="none"
        >
          <path
            fill="#F9FAFB"
            fillOpacity="1"
            d="M0,32L80,42.7C160,53,320,75,480,74.7C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,100L1360,100C1280,100,1120,100,960,100C800,100,640,100,480,100C320,100,160,100,80,100L0,100Z"
          ></path>
        </svg>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          {/* Main Title - H1 */}
          <h1 className="text-4xl md:text-6xl font-heading font-bold text-white drop-shadow-lg">
            {siteName}
          </h1>

          {/* Location and proximity info */}
          {city && (
            <SiteLocationInfo 
              city={city} 
              proximityInfo={proximityInfo}
            />
          )}

          {/* Services badges */}
          {services.length > 0 && (
            <SiteServicesDisplay services={services} />
          )}

          {/* Contact info for mini-sites */}
          {(phone || email || websiteUrl) && (
            <SiteMetaInfo
              siteName={siteName}
              city={city || ""}
              phone={phone}
              email={email}
              website={websiteUrl}
            />
          )}

          {/* CTA Button */}
          {websiteUrl && !isMiniSite && (
            <div className="flex justify-center pt-4">
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-fabre-green hover:bg-fabre-darkteal text-white font-semibold px-8 py-4 rounded-xl transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 transform"
              >
                <span>Visiter le site web</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-6m-7 1l9-9m0 0l-5 0m5 0v5" />
                </svg>
              </a>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default SiteDetailHero;
